
import { useState, useEffect } from 'react';
import {
  Phone,
  CheckCircle,
  ExternalLink,
  Zap,
  Shield,
  Plus,
  Trash2,
  Edit,
  Calendar,
  PhoneIncoming,
  PhoneCall,
  Activity
} from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';

import { getClientIntegrations, postKnowlarityNumber, deleteClientIntegration, Integration } from '@/actions/data-fetch-settings';
import InputField from '../../../components/common/InputField/InputField';
import Button from '../../../components/common/Button';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastMessage } from '@/utils/toast-message';

const schema = yup.object({
  integration_code: yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(10, 'Must be exactly 10 digits')
    .max(10, 'Must be exactly 10 digits')
}).required();

type FormData = {
  integration_code: string;
};

// Phone provider configurations
const phoneProviders = [
  {
    name: 'Knowlarity',
    description: 'Cloud telephony platform',
    color: 'blue',
    icon: <PhoneCall className="w-5 h-5" />
  },
  {
    name: 'Mcube',
    description: 'Business calling solution',
    color: 'green',
    icon: <PhoneIncoming className="w-5 h-5" />
  },
  {
    name: 'Ozonetel',
    description: 'Contact center platform',
    color: 'purple',
    icon: <Phone className="w-5 h-5" />
  },
  {
    name: 'TCPN',
    description: 'Telecom service provider',
    color: 'orange',
    icon: <Activity className="w-5 h-5" />
  }
];

const PhoneIntegrationPage = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      integration_code: ''
    }
  });

  // Fetch existing integrations
  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await getClientIntegrations();
      if (response.success && response.data) {
        // Filter phone integrations (type 2)
        const phoneIntegrations = response.data.integrations.filter(
          integration => integration.type === 2
        );
        setIntegrations(phoneIntegrations);
      }
    } catch (error: any) {
      console.error('Error fetching integrations:', error);
      toastMessage('Error fetching integrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitting(true);
      const params = {
        knowlarity_number: data.integration_code,
      };
      const response = await postKnowlarityNumber(params);
      toastMessage(response.message || 'Phone integration added successfully', 'success');
      reset({ integration_code: '' });
      // Refresh integrations list
      await fetchIntegrations();
    } catch (error: any) {
      toastMessage(error.message || 'Failed to add phone integration', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIntegration = async (integrationUuid: string) => {
    try {
      setDeletingId(integrationUuid);
      const response = await deleteClientIntegration(integrationUuid);
      if (response.success) {
        toastMessage('Integration deleted successfully', 'success');
        // Refresh integrations list
        await fetchIntegrations();
      } else {
        throw new Error(response.message || 'Failed to delete integration');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to delete integration', 'error');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm(null);
    }
  };

  const confirmDelete = (integrationUuid: string) => {
    setShowDeleteConfirm(integrationUuid);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const features = [
    {
      icon: <Phone className="w-6 h-6 text-blue-600" />,
      title: "Call Tracking",
      description: "Automatically capture leads from all incoming calls"
    },
    {
      icon: <Zap className="w-6 h-6 text-green-600" />,
      title: "Real-time Sync",
      description: "Instantly sync call data with your CRM dashboard"
    },
    {
      icon: <Shield className="w-6 h-6 text-purple-600" />,
      title: "Secure Integration",
      description: "Protected connections with trusted telephony platforms"
    }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Phone Integration"
        description="Connect your phone system to automatically capture leads from incoming calls across multiple providers"
        icon={<Phone className="h-5 w-5" />} />

      <div className="p-6">

      {/* Features Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Integration Benefits
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {feature.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Supported Providers */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Supported Phone Providers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {phoneProviders.map((provider, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 border-${provider.color}-200 dark:border-${provider.color}-800 bg-${provider.color}-50 dark:bg-${provider.color}-900/20 hover:shadow-md transition-shadow`}
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-${provider.color}-100 dark:bg-${provider.color}-900 text-${provider.color}-600 dark:text-${provider.color}-400 mb-3`}>
                {provider.icon}
              </div>
              <h4 className={`text-sm font-semibold text-${provider.color}-900 dark:text-${provider.color}-200 mb-1`}>
                {provider.name}
              </h4>
              <p className={`text-xs text-${provider.color}-700 dark:text-${provider.color}-300`}>
                {provider.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Add Integration Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Add Phone Integration
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Enter your 10-digit phone number or SR number from your phone provider dashboard
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="text"
              id="phone_number"
              name="integration_code"
              placeholder="Enter 10-digit phone number"
              label="Phone Number / SR Number"
              control={control}
              error={errors.integration_code?.message}
            />
            <div className="flex items-end">
              <Button
                type="submit"
                btnStyle="primary"
                className="w-full h-[42px]"
                disabled={submitting}
              >
                <div className="inline-flex items-center justify-center whitespace-nowrap">
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>Add Integration</span>
                    </>
                  )}
                </div>
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Current Integrations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Active Phone Integrations
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading integrations...</span>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-8">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No phone integrations found.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Add your first phone number to start capturing leads from calls.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {integrations.map((integration) => (
              <div
                key={integration.uuid}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-brandColor/50 dark:hover:border-brandColor/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-brandColor/10 dark:bg-brandColor/20 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-brandColor dark:text-brandColor" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Phone Number: {integration.code}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Added: {formatDate(integration.created_at)}
                      </span>
                      {integration.updated_at !== integration.created_at && (
                        <span className="flex items-center">
                          <Edit className="w-4 h-4 mr-1" />
                          Updated: {formatDate(integration.updated_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </span>
                  <button
                    onClick={() => confirmDelete(integration.uuid)}
                    disabled={deletingId === integration.uuid}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete integration"
                  >
                    {deletingId === integration.uuid ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-brandColor/10 dark:bg-brandColor/20 border border-brandColor/30 dark:border-brandColor/40 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-brandColor dark:text-brandColor mb-3 flex items-center">
          <ExternalLink className="w-5 h-5 mr-2" />
          How to Setup
        </h3>
        <div className="space-y-2 text-sm text-brandColor dark:text-brandColor">
          <p className="font-medium">Follow these steps to integrate your phone system:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Log in to your phone provider dashboard (Knowlarity, Mcube, Ozonetel, or TCPN)</li>
            <li>Find your 10-digit SR Number or Phone Number in the settings</li>
            <li>Copy the number and add it using the form above</li>
            <li>Configure the webhook URL in your provider's dashboard to point to our system</li>
            <li>Test the integration by making a call to verify lead capture</li>
          </ol>
          <p className="mt-3 pt-3 border-t border-brandColor/30 dark:border-brandColor/40">
            <strong>Need help?</strong> Contact your phone provider's support team for webhook configuration details.
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Integration
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to delete this phone integration?
                You will stop receiving leads from this number.
              </p>
              <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Phone Number: {integrations.find(i => i.uuid === showDeleteConfirm)?.code}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                btnStyle="secondary"
                onClick={cancelDelete}
                disabled={deletingId === showDeleteConfirm}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                type="button"
                btnStyle="redBtn"
                onClick={() => handleDeleteIntegration(showDeleteConfirm)}
                disabled={deletingId === showDeleteConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <div className="inline-flex items-center justify-center whitespace-nowrap">
                  {deletingId === showDeleteConfirm ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>Delete Integration</span>
                    </>
                  )}
                </div>
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
  );
};

export default PhoneIntegrationPage;
